import {
  ParameterSchema,
  formatParameterChoiceLabel,
  type ParamType,
} from '../../../models/pricing-model.model';

export interface ParameterEditorField {
  key: string;
  label: string;
  description: string;
  type: ParamType;
  hasChoices: boolean;
  choices: Array<{ label: string; value: string | null }>;
  defaultPlaceholder: string;
  min?: number;
  max?: number;
}

// The backend describes parameters in a generic schema format. The editor
// template should not need to understand those backend details, so we convert
// each schema entry into a render-ready field object once at the boundary.
export function buildEditorFieldsFromSchemas(
  parameterSchemas: Record<string, ParameterSchema>,
): ParameterEditorField[] {
  return Object.entries(parameterSchemas).map(([key, schema]) => {
    const choices = schema.choices ?? [];

    return {
      key,
      label: schema.label || schema.name,
      description: schema.description ?? '',
      type: schema.param_type,
      hasChoices: choices.length > 0,
      choices: choices.map((choice) => ({
        label: formatParameterChoiceLabel(choice),
        value: choice.value,
      })),
      defaultPlaceholder: formatDefaultPlaceholderForField(schema),
      min: parseOptionalNumericConstraint(schema.minimum),
      max: parseOptionalNumericConstraint(schema.maximum),
    };
  });
}

// Different screens receive parameter values from different places: API data,
// local component state, or partially edited form data. This helper reshapes
// that input into a predictable record with one entry per schema key so the
// rest of the UI can safely assume the same object structure everywhere.
export function buildSchemaBackedParameterValues(
  parameterSchemas: Record<string, ParameterSchema>,
  values: Record<string, string | null> | null | undefined,
): Record<string, string | null> {
  return Object.fromEntries(
    Object.keys(parameterSchemas).map((key) => [
      key,
      coerceParameterValueToStringOrNull(values?.[key]),
    ]),
  );
}

// Saving is the last point where we can guarantee consistent API payloads.
// We run every edited value through the same cleanup rule here so blank form
// values become nulls instead of being persisted as accidental empty strings.
export function sanitizeParameterValuesForSave(
  values: Record<string, string | null>,
): Record<string, string | null> {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      coerceParameterValueToStringOrNull(value),
    ]),
  );
}

// Default values are presented as placeholders in the editor. Keeping that
// formatting here ensures every screen explains defaults in the same wording,
// including special cases such as boolean labels and choice labels.
function formatDefaultPlaceholderForField(schema: ParameterSchema): string {
  const resolvedDefault = schema.resolved_default;
  if (resolvedDefault == null) {
    return 'Unset';
  }

  if (schema.param_type === 'string' && schema.choices?.length) {
    const defaultChoice = schema.choices.find(
      (choice) => choice.value === String(resolvedDefault),
    );
    if (defaultChoice) {
      return `Default: ${formatParameterChoiceLabel(defaultChoice)}`;
    }
  }

  if (schema.param_type === 'boolean') {
    return `Default: ${resolvedDefault ? 'Yes' : 'No'}`;
  }

  return `Default: ${resolvedDefault}`;
}

// Numeric limits sometimes arrive from the backend as strings and sometimes as
// numbers. PrimeNG input components only care about real numbers, so this keeps
// that conversion in one place instead of repeating Number(...) checks in the UI.
function parseOptionalNumericConstraint(
  value: number | string | null | undefined,
): number | undefined {
  if (value == null || value === '') {
    return undefined;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : undefined;
}

// Parameter values cross the API boundary as strings, but an empty string does
// not mean the same thing as a real value. We collapse undefined, null, and
// blank strings into a single null state so every caller shares the same
// definition of "parameter is not set".
function coerceParameterValueToStringOrNull(
  value: string | null | undefined,
): string | null {
  return value == null || value === '' ? null : String(value);
}
